import { MARKET } from '@/market'

/**
 * Samly pricing. There are no plans — the price is the sum of the components a
 * venue actually uses, denominated in price units and discounted by billing term.
 *
 * A unit is 1 BRV in Uzbekistan; other markets set its money value in src/market.
 * The `brv` fields below are unit weights, so every market keeps the same ratios.
 */
export const UNIT_VALUE = MARKET.pricing.unitValue

/** Billing terms. Nothing shorter than 3 months is sold. */
export const TERMS = [
  { months: 3, discount: 0.1 },
  { months: 6, discount: 0.2 },
  { months: 12, discount: 0.3 },
] as const

export type Term = (typeof TERMS)[number]

export type ComponentKey =
  | 'branches'
  | 'employees'
  | 'pos_devices'
  | 'online_orders'
  | 'waiter_app'
  | 'qr_menu'
  | 'yandex'
  | 'monitors'
  | 'warehouses'

export type PriceComponent = {
  key: ComponentKey
  /** Monthly price, in price units. */
  brv: number
  /** Smallest sellable quantity. */
  min?: number
  /** Sold per branch — quantity can never exceed the branch count. */
  perBranch?: boolean
}

const ALL_COMPONENTS: PriceComponent[] = [
  { key: 'branches', brv: 0.25, min: 1 },
  { key: 'employees', brv: 0.1, min: 1 },
  { key: 'pos_devices', brv: 0.1, min: 1 },
  { key: 'online_orders', brv: 0.5, perBranch: true },
  { key: 'waiter_app', brv: 0.25, perBranch: true },
  { key: 'qr_menu', brv: 0.1, perBranch: true },
  { key: 'yandex', brv: 0.25, perBranch: true },
  { key: 'monitors', brv: 0.075 },
  { key: 'warehouses', brv: 0.05 },
]

const unavailable = new Set<string>(MARKET.unavailable)

/** Components this market sells. */
export const COMPONENTS = ALL_COMPONENTS.filter(c => !unavailable.has(c.key))

/** Features every subscription includes at no extra charge. */
const ALL_INCLUDED_KEYS = [
  'kds',
  'manager_app',
  'loyalty_crm',
  'ai_analytics',
  'kiosk',
  'open_api',
  'stop_list',
  'cash_shift',
  'order_management',
  'basic_analytics',
  'order_status',
  'e_kassa',
  'pin_protection',
  'floor_plan',
  'owner_push',
  'qr_generator',
  'payment_types',
  'receipt_printer',
] as const

export type IncludedKey = (typeof ALL_INCLUDED_KEYS)[number]

export const INCLUDED_KEYS: readonly IncludedKey[] = ALL_INCLUDED_KEYS.filter(k => !unavailable.has(k))

export type Quantities = Record<ComponentKey, number>

/** The smallest set Samly sells: one branch, one seat, one register. */
export const MIN_QUANTITIES: Quantities = {
  branches: 1,
  employees: 1,
  pos_devices: 1,
  online_orders: 0,
  waiter_app: 0,
  qr_menu: 0,
  yandex: 0,
  monitors: 0,
  warehouses: 0,
}

/** A typical single-venue setup, used as the calculator's starting point. */
export const DEFAULT_QUANTITIES: Quantities = {
  branches: 1,
  employees: 6,
  pos_devices: 1,
  online_orders: 1,
  waiter_app: 1,
  qr_menu: 1,
  yandex: 0,
  monitors: 0,
  warehouses: 1,
}

export function findComponent(key: ComponentKey) {
  const found = COMPONENTS.find(c => c.key === key)
  if (!found) throw new Error(`Unknown pricing component: ${key}`)
  return found
}

/** Totals are floored to the nearest 0.05 unit, never rounded up. */
export function floorToStep(value: number, step = 0.05) {
  return Math.floor(value / step + 1e-9) * step
}

export function monthlyBrv(qty: Quantities) {
  return COMPONENTS.reduce((sum, c) => sum + c.brv * (qty[c.key] || 0), 0)
}

export type Quote = {
  perMonthBrv: number
  fullBrv: number
  totalBrv: number
  discountedPerMonthBrv: number
  savedBrv: number
}

export function quote(qty: Quantities, term: Term): Quote {
  const perMonthBrv = monthlyBrv(qty)
  const fullBrv = perMonthBrv * term.months
  const totalBrv = floorToStep(fullBrv * (1 - term.discount))

  return {
    perMonthBrv,
    fullBrv,
    totalBrv,
    discountedPerMonthBrv: totalBrv / term.months,
    savedBrv: fullBrv - totalBrv,
  }
}

const { fractionDigits } = MARKET.pricing
const moneyStep = 10 ** fractionDigits

/** Rounded to the market's smallest shown unit (whole sum, or tetri), so receipt lines add up. */
export function toMoney(brv: number) {
  return Math.round(brv * UNIT_VALUE * moneyStep) / moneyStep
}

const moneyFmt = new Intl.NumberFormat(MARKET.pricing.numberLocale, {
  minimumFractionDigits: fractionDigits,
  maximumFractionDigits: fractionDigits,
  // Whole amounts stay "23", not "23,00".
  trailingZeroDisplay: 'stripIfInteger',
} as Intl.NumberFormatOptions)

export function formatMoney(amount: number) {
  return moneyFmt.format(amount)
}

export const LONGEST_TERM = TERMS[TERMS.length - 1]

/**
 * The cheapest price we can honestly advertise: the minimum set on the longest
 * term. Used for the "from …" headline so it can never drift from the calculator.
 */
export function entryPricePerMonth() {
  return toMoney(quote(MIN_QUANTITIES, LONGEST_TERM).discountedPerMonthBrv)
}

/** Clamp a quantity to its own floor and, for per-branch items, the branch count. */
export function clampQuantity(key: ComponentKey, next: number, qty: Quantities) {
  const component = findComponent(key)
  let n = Math.max(component.min ?? 0, Math.floor(next))
  if (component.perBranch) n = Math.min(n, qty.branches)
  return n
}

/** Apply a quantity change, keeping per-branch caps consistent. */
export function setQuantity(qty: Quantities, key: ComponentKey, next: number): Quantities {
  const updated: Quantities = { ...qty, [key]: clampQuantity(key, next, qty) }

  if (key === 'branches') {
    for (const c of COMPONENTS) {
      if (c.perBranch && updated[c.key] > updated.branches) updated[c.key] = updated.branches
    }
  }

  return updated
}
